import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, errorMessage } from "../../lib/api";
import { inr } from "../landing/usePricing";

const t = (v) => (v ? new Date(v).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—");

const SlipActions = ({ g }) => {
  const link = async () => `${window.location.origin}/slip/${(await adminApi.post(`/admin/hk/payouts/${g.worker_id}/slip`)).data.token}`;
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const open = async (e) => { stop(e); const w = window.open("about:blank", "_blank"); try { const u = await link(); if (w) w.location.href = u; } catch (er) { if (w) w.close(); toast.error(errorMessage(er)); } };
  const wa = async (e) => {
    stop(e);
    const w = window.open("about:blank", "_blank");
    try {
      const u = await link();
      const text = `Balangir Group payout slip for ${g.full_name}: total ${inr(g.total)}, paid ${inr(g.paid)}, due ${inr(g.unpaid)}. ${u}`;
      if (w) w.location.href = `https://wa.me/91${g.mobile}?text=${encodeURIComponent(text)}`;
    } catch (er) { if (w) w.close(); toast.error(errorMessage(er)); }
  };
  const tg = async (e) => {
    stop(e);
    try { await adminApi.post(`/admin/hk/payouts/${g.worker_id}/slip/telegram`, { url: await link() }); toast.success("Slip sent on Telegram"); } catch (er) { toast.error(errorMessage(er)); }
  };
  const b = "border border-white/15 px-2 py-1 text-xs text-slate-200 hover:border-orange-400";
  return (
    <span className="flex gap-1">
      <button onClick={open} data-testid={`hk-slip-open-${g.reference}`} className={b}>Slip</button>
      <button onClick={wa} data-testid={`hk-slip-wa-${g.reference}`} className={b}>WhatsApp</button>
      <button onClick={tg} disabled={!g.telegram} title={g.telegram ? "" : "Employee has not connected Telegram"} data-testid={`hk-slip-tg-${g.reference}`} className={`${b} disabled:opacity-40`}>Telegram</button>
    </span>
  );
};

const Group = ({ g, onPaid }) => {
  const pay = (e) => {
    e.preventDefault();
    if (!window.confirm(`Mark ${inr(g.unpaid)} as paid to ${g.full_name}?`)) return;
    adminApi.post(`/admin/hk/payouts/${g.worker_id}/paid`).then((r) => { toast.success(`${inr(r.data.amount)} marked paid`); onPaid(); }).catch((er) => toast.error(errorMessage(er)));
  };
  return (
  <details data-testid={`hk-payout-${g.reference}`} className="border border-white/10">
    <summary className="flex cursor-pointer flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
      <span><b className="text-white">{g.full_name}</b> <span className="ml-2 font-mono text-xs text-orange-300">{g.reference}</span> · {g.mobile} · {g.shifts.length} shift(s)</span>
      <span className="flex flex-wrap items-center gap-3">
        <SlipActions g={g} />
        <span className="text-xs text-slate-400">Total <b className="font-mono text-white">{inr(g.total)}</b> · paid <b className="font-mono text-emerald-300">{inr(g.paid)}</b></span>
        {g.unpaid > 0 ? (
          <button onClick={pay} data-testid={`hk-payout-paid-${g.reference}`} className="bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700">Paid {inr(g.unpaid)}</button>
        ) : (
          <span data-testid={`hk-payout-settled-${g.reference}`} className="text-xs text-emerald-300">All paid{g.last_paid_at ? ` · ${new Date(g.last_paid_at).toLocaleDateString("en-IN")}` : ""}</span>
        )}
      </span>
    </summary>
    <div className="overflow-x-auto px-3 pb-3">
      <table className="w-full min-w-[760px] text-xs">
        <thead><tr className="text-left text-slate-400">
          {["Date", "Booking", "In", "Out", "Hours", "Wage", "Overtime", "Travel", "5★ bonus", "Total", "Status"].map((h) => <th key={h} className="py-1 pr-2 font-medium">{h}</th>)}
        </tr></thead>
        <tbody>
          {g.shifts.map((s) => (
            <tr key={s.booking + s.check_in} className="border-t border-white/5">
              <td className="py-1 pr-2">{s.date}</td><td className="py-1 pr-2">{s.booking} · {s.service}</td>
              <td className="py-1 pr-2">{t(s.check_in)}</td><td className="py-1 pr-2">{s.check_out ? t(s.check_out) : "working"}</td>
              <td className="py-1 pr-2">{s.hours ?? "—"}</td><td className="py-1 pr-2">{inr(s.wage)}</td><td className="py-1 pr-2">{inr(s.overtime_pay)}</td>
              <td className="py-1 pr-2">{inr(s.travel_allowance)}</td><td className="py-1 pr-2">{inr(s.bonus)}</td>
              <td className="py-1 pr-2 font-mono text-emerald-300">{inr(s.total_payout)}</td>
              <td className={`py-1 pr-2 ${s.paid ? "text-emerald-300" : "text-amber-300"}`}>{s.check_out ? (s.paid ? "Paid" : "Due") : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </details>
  );
};

export const HkPayouts = () => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => adminApi.get("/admin/hk/payouts").then((r) => setItems(r.data)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);
  if (!items) return <p className="py-6 text-center text-slate-500">Loading…</p>;
  const total = items.reduce((s, g) => s + g.total, 0);
  const due = items.reduce((s, g) => s + g.unpaid, 0);
  return (
    <div data-testid="hk-payouts" className="space-y-2">
      <p className="text-sm text-slate-300">Total employee payout: <b data-testid="hk-payouts-total" className="font-mono text-emerald-300">{inr(total)}</b> · still to pay: <b data-testid="hk-payouts-due" className="font-mono text-amber-300">{inr(due)}</b> · wage ₹700/8h + overtime ₹100/h + travel + ₹50 for 5★</p>
      {items.length === 0 && <p className="py-6 text-center text-slate-500">No shifts yet.</p>}
      {items.map((g) => <Group key={g.worker_id} g={g} onPaid={load} />)}
    </div>
  );
};
